import useWindowSize from "@DSG/Common/Hooks/useWindowSize";
import ChevronDownSmall from "@DSG/Icons/ChevronDownSmall";
import ChevronUpSmall from "@DSG/Icons/ChevronUpSmall";
import Close from "@DSG/Icons/Close";
import colorPallete, {
    bodyFonts,
    buttonFonts,
    elevation,
    headlineFonts,
    labelFonts,
    themeFontWeights,
} from "@DSG/MobiusOverrides/styles/variables";
import BrandAlphabetNavigation, {
    BrandAlphabetNavigationStyles,
} from  "@DSG/Overrides/Widgets/Brand/BrandAlphabetNavigation";
import isNumeric from "@insite/client-framework/Common/isNumeric";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getAllBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import translate from "@insite/client-framework/Translate";
import { BrandAlphabetLetterModel, BrandModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import VerticalColumnCell, { VerticalColumnCellProps } from "@insite/content-library/Components/VerticalColumnCell";
import VerticalColumnContainer, {
    VerticalColumnContainerProps,
} from "@insite/content-library/Components/VerticalColumnContainer";
import Accordion, { AccordionProps } from "@insite/mobius/Accordion";
import AccordionSection, {
    AccordionSectionPanel,
    AccordionSectionPresentationProps,
} from "@insite/mobius/AccordionSection";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import Search from "@insite/mobius/Icons/Search";
import { Clickable } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useRef, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    allBrandsDataView: getAllBrandsDataView(state),
    brandAlphabetState: state.pages.brands.brandAlphabetState,
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

type BrandLetterMap = {
    [letter: string]: BrandModel[];
};

export interface BrandListStyles {
    input?: TextFieldPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    container?: InjectableCss;
    brandListFilterWrapper?: InjectableCss;
    brandAlphabetNavigation?: BrandAlphabetNavigationStyles;
    brandAccordion?: AccordionProps;
    brandAccordionSection?: AccordionSectionPresentationProps;
    verticalColumnContainer?: VerticalColumnContainerProps;
    verticalColumnCell?: VerticalColumnCellProps;
    brandLink?: LinkPresentationProps;
    actions?: InjectableCss;
    actionLinks?: LinkPresentationProps;
    actionLinksDisabled?: TypographyPresentationProps;
    buttons?: InjectableCss;
    backToTopButton?: ButtonPresentationProps;
}

export const listStyles: BrandListStyles = {
    input: {
        sizeVariant: "default",
        iconProps: { src: Search, size: 24 },
        border: "underline",
        cssOverrides: {
            formField: css`
                width: 250px;
                transition: all 0.2s ease-in-out;
            `,
        },
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    container: {
        css: css`
            max-width: 1280px;
            margin: 0 auto;
        `,
    },
    brandListFilterWrapper: {
        css: css`
            display: flex;
            margin-bottom: 64px;

            .sticky-title-btn-wrapper {
                display: none;
            }

            .sticky-page-title {
                display: none;
            }

            > div {
                display: flex;
                flex-direction: column;
            }

            &.sticky {
                position: fixed;
                background: white;
                top: 0;
                left: 0;
                margin: 0;
                width: 100%;
                z-index: 9;
                box-shadow: ${elevation.dp04};
                padding: 24px 16px;

                .sticky-title-btn-wrapper {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 16px;

                    ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        css`
                            margin-bottom: 24px;
                            transition: all 1s ease;
                        `,
                        null,
                    ], "min")}
                }

                .close-backtotop {
                    font-size: ${buttonFonts.buttonLg.fontSize};
                    line-height: 19.8px;
                    font-weight: ${buttonFonts.buttonLg.fontWeight};

                    > span {
                        display: flex;
                        align-items: center;
                        column-gap: 8px;
                    }

                    [class*="IconWrapper"] {
                        & + span {
                            @media only screen and (max-width: 540px) {
                                display: none;
                            }
                        }
                    }
                }

                .sticky-page-title {
                    display: block;
                    margin-bottom: 0;
                    
                    ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        css`
                            transition: all 1s ease;
                        `,
                        null,
                    ], "min")}
                    
                }

                > div {
                    width: 100%;
                    max-width: 1280px;
                    margin: 0 auto;
                }

                & + .brandlist-accordion {
                  padding-top: 160px;
                }

                [class*="FormFieldStyle-"] {
                    ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                flex-basis: auto;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            > div {
                                flex-direction: column;
                                row-gap: 16px;
                            }
                            margin-bottom: 24px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}

            [class*="FormFieldStyle-"] {
                width: 100%;
                flex-basis: 412px;
                max-width: 412px;
                flex-shrink: 0;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                flex-basis: auto;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}

                > div {
                    margin-top: 0;
                }
                input,
                input:not(.react-datetime-picker__inputGroup__input) {
                    padding: 8px 8px 8px 40px;
                    border: 1px solid ${colorPallete.grey400};
                    border-radius: 64px;
                    min-height: 48px;
                    &:focus {
                        padding: 8px 8px 8px 40px;
                        border: 1px solid ${colorPallete.grey400};
                        border-radius: 64px;
                        min-height: 48px;
                    }

                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                null,
                                css`
                                    font-size: ${bodyFonts.body1RegularSm.fontSize};
                                    line-height: ${bodyFonts.body1RegularSm.lineHeight};
                                `,
                                null,
                                null,
                            ],
                            "max",
                        )}
                }
                [class*="IconWrapper"] {
                    left: 4px;
                    top: 2px;
                    right: unset;
                    width: 40px;
                    height: 40px;
                    svg {
                        width: 24px;
                        height: 24px;
                        color: ${colorPallete.primaryRed500};
                    }
                }
            }
        `,
    },
    brandAlphabetNavigation: {
        alphabetNavigation: {
            container: {
                css: css`
                    gap: 8px;
                    justify-content: flex-start;

                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                null,
                                css`
                                    margin-bottom: 24px;
                                `,
                            ],
                            "max",
                        )}
                `,
            },
            navigationItem: {
                css: css`
                    p {
                        font-weight: ${themeFontWeights.semiBold};
                        font-size: ${bodyFonts.body1SemiBoldLg.fontSize};
                        line-height: ${bodyFonts.body1SemiBoldLg.lineHeight};
                        color: ${colorPallete.grey800};
                        width: 40px;
                        height: 40px;
                        background: ${colorPallete.grey100};
                        border-radius: 8px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 8px;

                        &.navigation-empty {
                            background: ${colorPallete.grey300};
                            color: ${colorPallete.grey800};
                        }

                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(
                                theme,
                                [
                                    null,
                                    css`
                                        font-size: ${bodyFonts.body1SemiBoldSm.fontSize};
                                        line-height: ${bodyFonts.body1SemiBoldSm.lineHeight};
                                    `,
                                    null,
                                    null,
                                ],
                                "max",
                            )}
                    }

                    &:hover {
                        p {
                            background: ${colorPallete.grey300};
                        }
                    }
                `,
            },
            navigationTextEmpty: {
                color: colorPallete.grey800,
                weight: themeFontWeights.semiBold,
                size: bodyFonts.body1SemiBoldLg.fontSize,
                lineHeight: bodyFonts.body1SemiBoldLg.lineHeight,
            },
        },
    },
    brandAccordionSection: {
        mergeCss: true,
        collapseIconProps: {
            src: ChevronUpSmall,
            css: css`
                display: none;
            `
        },
        expandIconProps: {
            src: ChevronDownSmall,
            css: css`
                display: none;
            `
        },
        headerProps: {
            css: css`
                button {
                    border: 1px solid ${colorPallete.grey300};
                    border-left: none;
                    border-right: none;
                    background: ${colorPallete.white};
                    padding: 16px 0 24px;
                    border-bottom: none;

                    > ${IconWrapper} {
                        display: none;
                    }

                    &:focus {
                        padding: 16px 0 24px !important;
                        border-left: none !important;
                        border-right: none !important;
                        border-bottom-width: 0;
                    }
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                null,
                                css`
                                    &,
                                    &:focus {
                                        padding: 8px 0 !important;
                                    }
                                `,
                            ],
                            "max",
                        )}
                }
            `,
        },
        panelProps: {
            css: css`
                padding: 0 0 24px;
                border: none;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                padding: 0 0 8px;
                            `,
                        ],
                        "max",
                    )}
            `,
        },
        titleTypographyProps: {
            size: headlineFonts.headline2SemiBoldLg.fontSize,
            lineHeight: headlineFonts.headline2SemiBoldLg.lineHeight,
            weight: "bold",
            fontFamily: headlineFonts.headline1SemiBoldLg.fontFamily,
            css: css`
                text-transform: uppercase;
                color: ${colorPallete.grey800};

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                font-size: ${headlineFonts.headline2SemiBoldSm.fontSize};
                                line-height: ${headlineFonts.headline2SemiBoldSm.lineHeight};
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
            `,
        },
        css: css`
            [class*="TransitionManager"] {
                bottom: unset;
            }
        `,
    },
    backToTopButton: {
        variant: "secondary",
        sizeVariant: "small",
        css: css`
            margin: 0;
        `,
    },
    brandLink: {
        css: css`
            width: 100%;
            display: flex;
            font-size: ${bodyFonts.body2RegularSm.fontSize};
            line-height: ${bodyFonts.body2RegularSm.lineHeight};
            color: ${colorPallete.grey800};
            padding: 8px 0;

            &:hover {
                font-weight: ${themeFontWeights.semiBold};
                text-decoration: underline;
            }
        `,
    },
    verticalColumnContainer: {
        columnCounts: [1, 2, 2, 3, 4],
    },
    actions: {
        css: css`
            display: flex;
            justify-content: flex-end;
            padding: 5px;
        `,
    },
    actionLinks: {
        css: css`
            margin: 0 5px 5px;
        `,
    },
    actionLinksDisabled: {
        css: css`
            margin: 0 5px 5px;
        `,
        color: "text.disabled",
    },
    buttons: {
        css: css`
            display: flex;
            justify-content: flex-end;
        `,
    },
};

const styles = listStyles;

interface BrandListState {
    expanded: boolean | "mixed";
    brandLetterDetails: BrandAlphabetLetterModel[];
    brandSections: BrandSection[];
}

/**
 * This will display all Brands grouped by the first character.
 * Starting with numbers grouped into '#'.
 * @param props
 */
const BrandList: FC<Props> = (props: Props) => {
    const { allBrandsDataView, brandAlphabetState } = props;

    const brandList = allBrandsDataView.value || [];
    const brandAlphabet = brandAlphabetState.value || [];
    const { letters, brandLettersMap } = buildLettersAndMap(brandList);
    const { brandLetterDetails, brandSections } = buildBrandSections(letters, brandLettersMap, brandAlphabet, true);
    const [brandMap] = brandSections;
    const headerRef = useRef<HTMLDivElement>(null);

    const [state, setState] = React.useState<BrandListState>({ expanded: true, brandLetterDetails, brandSections });
    const [lastLetterClicked, setlastLetterClicked] = React.useState("");
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [headerVisible, setHeaderVisible] = useState(false);

    const {width} = useWindowSize();
    let firstOffset = 270;
    let secondOffset = 260; 
    if(width < 768) {
        firstOffset = 255;

        secondOffset =  325;
    }
    React.useEffect(() => {
        setState({ ...state, brandLetterDetails, brandSections });
    }, [brandLetterDetails?.length, brandSections?.length]);

    React.useLayoutEffect(() => {
        if (lastLetterClicked) {
            const letterElement = document.getElementById(`letter-${lastLetterClicked}`);
            const lastLetterElement = document.querySelector(`[data-test-key="${lastLetterClicked}"]`);
            let scrollDistance = 0;
            if (headerRef.current !== null && lastLetterElement !== null) {
                scrollDistance = headerRef.current?.offsetHeight;
            }
            if (letterElement) {
                const letterOffset = letterElement.offsetTop;
                if (letterOffset > 0) {
                    window.scrollTo(0, letterOffset - scrollDistance);
                }
            }
            setlastLetterClicked("");
        }
    }, [lastLetterClicked]);

    React.useEffect(() => {
        function handleScroll() {
            if (!headerRef.current) {return;}
            const headerHeight = headerRef.current.offsetHeight === 0 ? 225 : headerRef.current.offsetHeight + 25;
            const accordion = document.querySelector('.brandlist-accordion') as HTMLElement;
            const headerEleHeight = document.querySelector('header') as HTMLElement;
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          

            if (accordion) {
                const top = (accordion.offsetTop - headerHeight) ;
                const bottom = top + accordion.offsetHeight;

                if (scrollTop >= headerEleHeight.offsetHeight) {
                    setHeaderVisible(true);
                } else {
                    setHeaderVisible(false);
                }
              
            }
        }

        function handleResize() {
            handleScroll();
        }

        window.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', handleResize);
        handleScroll(); 
        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
            document.body.classList.remove('hasPDPStickyHeader');
        };
    }, []);

    const handleBrandSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value);
    }

    if (allBrandsDataView.isLoading || brandAlphabetState.isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }
    if (brandList.length === 0) {
        return null;
    }

    const onClickBrandSection = (section: BrandSection) => () => {
        setState(({ brandSections, expanded, brandLetterDetails }: BrandListState) => {
            const itemIndex = brandSections.findIndex(item => item.letter === section.letter);
            const nextSectionExpanded = !section.expanded;
            let nextExpanded = expanded;
            if (expanded !== "mixed" && expanded !== nextSectionExpanded) {
                nextExpanded = "mixed";
            } else if (expanded === "mixed") {
                const hasNonMatchingExpanded = brandSections.find(item => {
                    return item.letter !== section.letter && item.expanded !== nextSectionExpanded;
                });
                if (!hasNonMatchingExpanded) {
                    nextExpanded = nextSectionExpanded;
                }
            }
            const newSectionState = { ...section, expanded: nextSectionExpanded };
            const newBrandSections = [...brandSections];
            newBrandSections.splice(itemIndex, 1, newSectionState);
            return { brandSections: newBrandSections, expanded: nextExpanded, brandLetterDetails };
        });
    };

    const onCollapse = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (state.expanded === "mixed" || state.expanded) {
            const { letters, brandLettersMap } = buildLettersAndMap(brandList);
            const { brandLetterDetails, brandSections } = buildBrandSections(
                letters,
                brandLettersMap,
                brandAlphabet,
                false,
            );
            setState({ ...state, brandLetterDetails, brandSections, expanded: false });
        }
    };

    const onExpand = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (state.expanded === "mixed" || !state.expanded) {
            const { letters, brandLettersMap } = buildLettersAndMap(brandList);
            const { brandLetterDetails, brandSections } = buildBrandSections(
                letters,
                brandLettersMap,
                brandAlphabet,
                true,
            );
            setState({ ...state, brandLetterDetails, brandSections, expanded: true });
        }
    };

    const handleBackToTop = () => {
        window.scrollTo(0, 0);
        
        const buttonHashList = document.querySelectorAll('[data-test-selector="alphabetNavigationItemLetter"]') as NodeListOf<HTMLElement>;
        buttonHashList.forEach((buttonHash) => {
            buttonHash.classList.remove('active-brand');
        });
        
    };

    const handleBrandLetterClick = (letter: string) => {
        const section = state.brandSections.find(item => item.letter === letter);
        const letterElement = document.getElementById(`letter-${letter}`);
        const checkClass = document.getElementById('brandlist-filter-wrapper') as HTMLElement;
        const subHeaderHeight = document.querySelector('header .shipAuthMsgWrapper') as HTMLElement;
        let subHeaderHeightOffset = 0;
        if (headerRef.current !== null && letterElement) {
            if(width < 768 && subHeaderHeight) {
               
                if(subHeaderHeight.classList.contains('authMsg')) {
                    if(!checkClass.classList.contains('sticky')) {
                        subHeaderHeightOffset = subHeaderHeight.offsetHeight;
                    } else {
                        subHeaderHeightOffset = subHeaderHeight.offsetHeight + 5;
                    }
                } else {
                    if(!checkClass.classList.contains('sticky')) {
                        subHeaderHeightOffset = subHeaderHeight.offsetHeight - 35;
                    } else {
                        subHeaderHeightOffset = subHeaderHeight.offsetHeight - 32;
                    }
                }
            }
            window.scrollTo(0,letterElement?.offsetTop - firstOffset + subHeaderHeightOffset);
          
            if(checkClass && !checkClass.classList.contains('sticky')) {
                window.scrollTo(0,letterElement?.offsetTop - secondOffset + subHeaderHeightOffset);
            }
        }
        setTimeout(() => {
            const buttonHash = document.querySelector('[data-test-selector="alphabetNavigationItemLetter"]') as HTMLElement;
            if(buttonHash.classList.contains('active-brand')) {
                setHeaderVisible(true);
            }
        }, 1000);
      
    };

    return (
        <StyledWrapper {...styles.container} data-test-selector="brandList">
            <StyledWrapper id="brandlist-filter-wrapper" className={`brandlist-filter-wrapper ${headerVisible ? 'sticky' : ''}`} {...styles.brandListFilterWrapper} ref={headerRef}>
                <StyledWrapper className="brandlist-letters-wrapper">
                    <StyledWrapper className="sticky-title-btn-wrapper">
                        <Typography as="h1" variant="h1" className="sticky-page-title">Shop by Brand</Typography>
                        <Clickable className="close-backtotop" onClick={handleBackToTop}>
                            <Icon src={Close} size={24} />
                            <Typography>Close and back to top</Typography>
                        </Clickable>
                    </StyledWrapper>
                    <BrandAlphabetNavigation
                        letterDetails={brandLetterDetails}
                        extendedStyles={styles.brandAlphabetNavigation}
                        onBrandLetterClick={handleBrandLetterClick}
                    />
                </StyledWrapper>
            </StyledWrapper>
            {/* Commented for future use */}
            {/* <StyledWrapper {...styles.actions}>
                {state.expanded === false ? (
                    <Typography {...styles.actionLinksDisabled}>{translate("Collapse All")}</Typography>
                ) : (
                    <Link
                        onClick={onCollapse}
                        {...styles.actionLinks}
                        id="collapseButton"
                        data-test-selector="brandListCollapseAllLink"
                    >
                        {translate("Collapse All")}
                    </Link>
                )}
                {state.expanded === true ? (
                    <Typography {...styles.actionLinksDisabled}>{translate("Expand All")}</Typography>
                ) : (
                    <Link
                        onClick={onExpand}
                        {...styles.actionLinks}
                        id="expandButton"
                        data-test-selector="brandListExpandAllLink"
                    >
                        {translate("Expand All")}
                    </Link>
                )}
            </StyledWrapper> */}
            <Accordion headingLevel={2} className="brandlist-accordion">
                {state.brandSections.map(brandDetails => (
                    <AccordionSection
                        key={brandDetails.letter}
                        {...styles.brandAccordionSection}
                        title={brandDetails.letter}
                        expanded={true}
                        onTogglePanel={onClickBrandSection(brandDetails)}
                    >
                        <VerticalColumnContainer
                            id={`letter-${brandDetails.letter}`}
                            {...styles.verticalColumnContainer}
                            data-test-selector="brandSection"
                            columnCounts={[1, 1, 2, 3, 4]}
                        >
                            {brandDetails.brandMap.map(brand => (
                                <VerticalColumnCell key={brand.detailPagePath} {...styles.verticalColumnCell}>
                                    <Link
                                        {...styles.brandLink}
                                        href={brand.detailPagePath}
                                        typographyProps={{ ellipsis: true }}
                                        data-test-selector="brandLink"
                                    >
                                        {brand.name}
                                    </Link>
                                </VerticalColumnCell>
                            ))}
                        </VerticalColumnContainer>
                    </AccordionSection>
                ))}
            </Accordion>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(BrandList),
    definition: {
        group: "Brands",
        icon: "List",
        allowedContexts: ["BrandsPage"],
    },
};

export default widgetModule;

interface BrandSection {
    letter: string;
    brandMap: BrandModel[];
    expanded: boolean;
}

/**
 * Takes in a brand list and sorts it into #, A-Z, Other characters.
 *
 * @param brandList The list of brands that should be sorted.
 */
const buildLettersAndMap = (brandList: BrandModel[]): { letters: string[]; brandLettersMap: BrandLetterMap } => {
    let letters: string[] = [];
    let letter: string;
    let newLetters: string[] = letters;
    const brandLettersMap: BrandLetterMap = {};
    for (let i = 0; i < brandList.length; i = i + 1) {
        const brand = brandList[i];
        letter = brand.name[0] ? brand.name[0].toLowerCase() : "";
        if (isNumeric(letter)) {
            letter = "#";
        }
        if (!brandLettersMap[letter]) {
            brandLettersMap[letter] = [];
        }
        brandLettersMap[letter].push(brand);
        newLetters = [...newLetters, letter];
    }
    letters = newLetters.filter((letter, index, array) => array.indexOf(letter) === index);
    if (brandLettersMap["#"]) {
        brandLettersMap["#"].sort((a, b) => parseInt(a.name, 10) - parseInt(b.name, 10));
    }

    return { letters, brandLettersMap };
};

/**
 * Take in the props and create easier to work with state objects.
 *
 * @param letters List of letters and special characters.
 * @param brandLettersMap Brand list sorted into letter lists.
 * @param brandAlphabet Letter count list.
 * @param expanded The expanded value to use if not undefined.
 */
const buildBrandSections = (
    letters: string[],
    brandLettersMap: BrandLetterMap,
    brandAlphabet: BrandAlphabetLetterModel[],
    expanded: boolean | undefined,
): {
    brandLetterDetails: {
        count: number;
        letter: string;
        linkable: boolean;
    }[];
    brandSections: {
        letter: string;
        brandMap: BrandModel[];
        expanded: boolean;
    }[];
} => {
    const brandLetterDetails = brandAlphabet.map(alphabetItem => ({
        ...alphabetItem,
        linkable: alphabetItem.count > 0,
    }));
    const brandSections = letters.map(letter => ({
        letter,
        brandMap: brandLettersMap[letter],
        expanded: expanded || false,
    }));

    return { brandLetterDetails, brandSections };
};
